import { Suspense } from 'react';
import { useSearchParams } from '@/hooks/use-routing';
import Link from '@/components/smart-link';
import { SharpeLogo } from '@sharpe/ui/components';

function AuthErrorContent() {
  const searchParams = useSearchParams();
  const error = searchParams.get('error');

  const errorMessages: Record<string, string> = {
    Configuration: 'There is a problem with the server configuration.',
    AccessDenied: 'Access denied. You do not have permission to sign in.',
    Verification:
      'The verification link has expired or has already been used. Please request a new one.',
    EmailSignin: 'Failed to send the verification email. Please try again.',
    OAuthSignin: 'Error occurred during OAuth sign in.',
    OAuthCallback: 'Error occurred during OAuth callback.',
    OAuthCreateAccount: 'Could not create OAuth provider user.',
    EmailCreateAccount: 'Could not create email provider user.',
    Callback: 'Your session is no longer valid. Please sign in again.',
    SessionRequired:
      'Your session has expired or the authentication method has changed. Please sign in again.',
    OAuthAccountNotLinked: 'Email is already linked to another account.',
    Default: 'An unexpected error occurred during authentication.',
  };

  const message = error ? errorMessages[error] || errorMessages.Default : errorMessages.Default;

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md space-y-8 p-8 text-center">
        <div>
          <SharpeLogo size={12} className="mx-auto" />
          <h2 className="mt-6 text-3xl font-semibold tracking-tight">Authentication Error</h2>
          <p className="mt-4 text-md text-muted-foreground">{message}</p>
          {error && <p className="mt-2 text-xs text-muted-foreground">Error code: {error}</p>}
        </div>
        <div className="mt-8">
          <Link
            href="/api/auth/signin"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-background text-foreground hover:bg-background/90 h-10 px-4 py-2"
          >
            Try Again
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AuthErrorPage() {
  return (
    <Suspense
      fallback={<div className="flex min-h-screen items-center justify-center">Loading...</div>}
    >
      <AuthErrorContent />
    </Suspense>
  );
}
